// The lab screen: the capture machine and the submission machine side by
// side, composed here so capture never knows about the network and a failed
// submit retries with the same recording (packages/speech/src/capture/README.md
// § Submitting).
//
// react-useeffect-discipline: start, stop, redo and submit happen in the
// handlers below (§1.5, §1.6); the only derived state kept is the last kept
// recording, adjusted during render (§1.4) so the device report outlives a
// redo.
import { fetchToken } from "@bindery/auth-consumer/browser";
import { reduceSubmission, submitRecording } from "@bindery/speech/client";
import { type Recording, useSpeechCapture } from "@bindery/speech/capture";
import { DEFAULT_CAPTURE_LIMITS } from "@bindery/speech/contract";
import { useReducer, useRef, useState } from "react";
import { CapturePanel } from "./components/capture-panel";
import { DeviceReportPanel } from "./components/device-report-panel";
import { PromptPanel } from "./components/prompt-panel";
import { ResultPanel, type RoundTrip } from "./components/result-panel";
import { guessBrowser } from "./lib/device-report";
import { useObjectUrl } from "./lib/hooks";
import {
  initialPrompt,
  measurePrompt,
  newItemId,
  type PromptFields,
} from "./lib/prompt";

// Must match TRANSCRIBE_PATH in worker/index.ts.
const TRANSCRIBE_URL = "/api/speech/transcribe";

export function Lab() {
  const capture = useSpeechCapture({ limits: DEFAULT_CAPTURE_LIMITS });
  const [submission, dispatch] = useReducer(reduceSubmission, { tag: "idle" });
  const [roundTrip, setRoundTrip] = useState<RoundTrip | null>(null);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [userAgent] = useState(() => navigator.userAgent);

  const recording: Recording | null =
    capture.state.tag === "recorded" ? capture.state.recording : null;
  const [lastRecording, setLastRecording] = useState<Recording | null>(null);
  if (recording !== null && recording !== lastRecording)
    setLastRecording(recording);

  const playbackUrl = useObjectUrl(recording?.blob ?? null);

  const promptOk = measurePrompt(prompt.promptText).withinLimits;
  const canSubmit =
    recording !== null &&
    promptOk &&
    (submission.tag === "idle" || submission.tag === "failed");

  // The submit whose answer the result panel is waiting for. A reset (start
  // or redo) aborts it and forgets it, so a slow answer to an earlier
  // recording can never land as the result of a later one — the submission
  // machine carries no request id to tell them apart.
  const inFlight = useRef<AbortController | null>(null);

  const clearResult = () => {
    inFlight.current?.abort();
    inFlight.current = null;
    dispatch({ type: "reset" });
    setRoundTrip(null);
  };

  const onStart = () => {
    clearResult();
    capture.start();
  };

  const onRedo = () => {
    clearResult();
    capture.redo();
  };

  const onSubmit = async () => {
    if (recording === null || !promptOk) return;
    // The reducer answers a second tap with the same state; skip the POST.
    if (
      reduceSubmission(submission, { type: "submit-requested" }) === submission
    )
      return;
    dispatch({ type: "submit-requested" });
    const startedAt = performance.now();
    setRoundTrip({ startedAt, endedAt: null });
    const attempt = prompt.attempt;
    const controller = new AbortController();
    inFlight.current = controller;
    const response = await submitRecording({
      url: TRANSCRIBE_URL,
      recording,
      fields: {
        promptText: prompt.promptText,
        itemRef: { kind: prompt.itemKind, itemId: prompt.itemId },
        language: prompt.language,
        attempt,
      },
      getToken: fetchToken,
      signal: controller.signal,
    });
    // The next submit for this item is the next attempt, whatever this one
    // answered — the tester can set it back by hand, or start a new item.
    // Counted even when the answer is stale: the POST may have gone out.
    setPrompt((p) =>
      p.attempt === attempt ? { ...p, attempt: attempt + 1 } : p,
    );
    if (inFlight.current !== controller) return;
    inFlight.current = null;
    setRoundTrip({ startedAt, endedAt: performance.now() });
    dispatch({ type: "response-received", response });
  };

  const onNewItem = () => {
    setPrompt((p) => ({ ...p, itemId: newItemId(), attempt: 1 }));
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      <div className="flex flex-col gap-4">
        <PromptPanel
          fields={prompt}
          onChange={(patch: Partial<PromptFields>) =>
            setPrompt((p) => ({ ...p, ...patch }))
          }
          onNewItem={onNewItem}
        />
        <CapturePanel
          capture={capture}
          playbackUrl={playbackUrl}
          canSubmit={canSubmit}
          submitting={submission.tag === "submitting"}
          onStart={onStart}
          onStop={capture.stop}
          onRedo={onRedo}
          onSubmit={() => void onSubmit()}
        />
      </div>
      <div className="flex flex-col gap-4">
        <ResultPanel submission={submission} roundTrip={roundTrip} />
        <DeviceReportPanel
          report={{
            userAgent,
            browser: guessBrowser(userAgent),
            capability: capture.capability,
            recording: lastRecording?.meta ?? null,
          }}
        />
      </div>
    </div>
  );
}
